import {MarkerEntry, Point} from '../../types'

// Markers within this distance (pixels) are considered overlapping
export const OVERLAP_THRESHOLD_PX = 8

// How far to spread overlapping markers from their centroid
export const SPREAD_RADIUS_PX = 4

const distanceBetweenPoints = (a: Point, b: Point): number => {
  const dx = a.x - b.x
  const dy = a.y - b.y
  return Math.sqrt(dx * dx + dy * dy)
}

/**
 * Groups markers that are within the overlap threshold into clusters.
 * Uses simple clustering: each marker joins an existing cluster if close to any member.
 */
export const findOverlappingClusters = (
  markers: MarkerEntry[],
  threshold = OVERLAP_THRESHOLD_PX
): MarkerEntry[][] => {
  const clusters: MarkerEntry[][] = []

  for (const marker of markers) {
    let addedToCluster = false

    for (const cluster of clusters) {
      const isCloseToAny = cluster.some(
        (m) => distanceBetweenPoints(m.position, marker.position) <= threshold
      )
      if (isCloseToAny) {
        cluster.push(marker)
        addedToCluster = true
        break
      }
    }

    if (!addedToCluster) {
      clusters.push([marker])
    }
  }

  return clusters
}

/**
 * Spreads overlapping markers radially around their centroid.
 * Single markers remain unchanged; clusters get distributed in a circle.
 */
export const spreadOverlappingMarkers = (
  locationMap: Record<string, Point>,
  options: {threshold?: number; radius?: number} = {}
): Record<string, Point> => {
  const {threshold = OVERLAP_THRESHOLD_PX, radius = SPREAD_RADIUS_PX} = options

  const markers: MarkerEntry[] = Object.entries(locationMap).map(([plantId, position]) => ({
    plantId,
    position
  }))

  const clusters = findOverlappingClusters(markers, threshold)
  const result: Record<string, Point> = {}

  for (const cluster of clusters) {
    if (cluster.length === 1) {
      result[cluster[0].plantId] = cluster[0].position
      continue
    }

    // Calculate centroid of the cluster
    const centroidX = cluster.reduce((sum, m) => sum + m.position.x, 0) / cluster.length
    const centroidY = cluster.reduce((sum, m) => sum + m.position.y, 0) / cluster.length

    // Spread markers radially around the centroid
    const angleStep = (2 * Math.PI) / cluster.length
    cluster.forEach((marker, index) => {
      const angle = index * angleStep - Math.PI / 2 // Start from top
      result[marker.plantId] = {
        x: Math.round(centroidX + radius * Math.cos(angle)),
        y: Math.round(centroidY + radius * Math.sin(angle))
      }
    })
  }

  return result
}
